import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Row from "./Row";
import useLang from "Shared/hooks/useLanguage";
import FacilityService from "Services/FacilityService/FacilityService";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import { StringRecord } from "Shared/Type";
import { SortingTypeI } from "Utils/consts";
import useIsMobile from "Shared/hooks/useIsMobile";
export interface IRows {
  labId: number;
  templateName: string;
  rowStatus: boolean | undefined;
  templateId: number;
  isActive?: boolean;
}

interface DropDownValues {
  referenceLab: Array<{
    value: number;
    label: string;
  }>;
}

interface ValidationState {
  TemplateName: string;
  Lab: string;
}

interface SearchRequest {
  labId: number;
  templateName: string;
  LabName: string;
}

// Constants
const INITIAL_SORTING_TYPE = {
  clickedIconData: "labId",
  sortingOrder: "desc",
} as const;

const INITIAL_SEARCH_QUERY = {
  labId: 0,
  templateName: "",
  LabName: "",
} as const;

const QUERY_DISPLAY_TAG_NAMES: StringRecord = {
  labId: "Lab",
  templateName: "Template Name",
  LabName: "Lab Name",
} as const;

// const PAGE_SIZE_OPTIONS = [5, 10, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const REQ_TYPE_ID = 58;

// Query keys for React Query
const QUERY_KEYS = {
  templateSettings: (params: any) => ["templateSettings", params],
  referenceLabs: ["referenceLabs"],
} as const;

interface TemplateData {
  rows: IRows[];
  total: number;
}

export default function OncologyPreConfig() {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [searchRequest, setSearchRequest] =
    useState<SearchRequest>(INITIAL_SEARCH_QUERY);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);

  // Sorting state
  const [sort, setSorting] = useState<SortingTypeI>(INITIAL_SORTING_TYPE);

  // Pagination states
  const [curPage, setCurPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);

  // Search request that triggers API call (only updated on button click)
  const [debouncedSearchRequest, setDebouncedSearchRequest] = useState<SearchRequest>(INITIAL_SEARCH_QUERY);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);


  const queryParams = useMemo(() => {
    const trimmedSearchRequest = Object.fromEntries(
      Object.entries(debouncedSearchRequest).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    return {
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel: {
        labId: trimmedSearchRequest.labId || 0,
        templateName: trimmedSearchRequest.templateName || "",
        LabName: trimmedSearchRequest.LabName || "",
        ReqTypeId: REQ_TYPE_ID,
      },
      sortColumn: sort?.clickedIconData || INITIAL_SORTING_TYPE.clickedIconData,
      sortDirection: sort?.sortingOrder || INITIAL_SORTING_TYPE.sortingOrder,
    };
  }, [curPage, pageSize, debouncedSearchRequest, sort]);


  const {
    data: templateData,
    isLoading: loading,
    isFetching,
  } = useQuery<AxiosResponse<any>, Error, TemplateData>(
    QUERY_KEYS.templateSettings(queryParams),
    () => FacilityService.GetTemplateSetting(queryParams),
    {
      keepPreviousData: false,
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error: any) => {
        console.error("Error fetching template settings:", error);
        toast.error(t("Error fetching template settings"));
      },
      select: (response: AxiosResponse<any>): TemplateData => ({
        rows: response?.data?.data || [],
        total: response?.data?.total || 0,
      }),
    }
  );

  const rows = templateData?.rows || [];
  const total = templateData?.total || 0;

  // React Query: Fetch reference labs
  const { data: referenceLabsData } = useQuery<AxiosResponse<any>, Error, DropDownValues>(
    QUERY_KEYS.referenceLabs,
    () => FacilityService.referenceLabLookup(),
    {
      staleTime: 300000,
      cacheTime: 600000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error: any) => {
        console.error("Error fetching reference labs:", error);
      },
      select: (response: AxiosResponse<any>): DropDownValues => {
        const referenceArray =
          response?.data?.data?.map((val: any) => ({
            value: val?.labId,
            label: val?.labDisplayName,
          })) || [];
        return { referenceLab: referenceArray };
      },
    }
  );

  const dropDownValues: DropDownValues = referenceLabsData || { referenceLab: [] };

  // Calculate pagination
  useEffect(() => {
    const calculatedTotalPages = Math.ceil(total / pageSize);
    setTotalPages(calculatedTotalPages);
    const pgNumbers = [];
    for (let i = curPage - 2; i <= curPage + 2; i++) {
      if (i > 0 && i <= calculatedTotalPages) {
        pgNumbers.push(i);
      }
    }
    setPageNumbers(pgNumbers);
  }, [total, curPage, pageSize]);

  const nextPage = () => {
    if (curPage < Math.ceil(total / pageSize)) {
      setCurPage(curPage + 1);
    }
  };
  const showPage = (i: number) => {
    setCurPage(i);
  };
  const prevPage = () => {
    if (curPage > 1) {
      setCurPage(curPage - 1);
    }
  };

  // Form states
  const [request, setRequest] = useState(false);
  const [check, setCheck] = useState(false);
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const [errors, setErrors] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({
    TemplateName: "",
    Lab: "",
  });

  // Local rows state for editing (optimistic updates)
  const [localRows, setLocalRows] = useState<IRows[]>([]);

  // Sync local rows with query data (including empty results after search)
  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  // React Query Mutation: Save template setting
  const saveTemplateMutation = useMutation<
    AxiosResponse<any>,
    Error,
    IRows
  >(
    (row: IRows) => {
      const queryModel = {
        templateId: row.templateId,
        labId: row.labId,
        templateName: row.templateName,
        isActive: row.isActive,
        ReqTypeId: REQ_TYPE_ID,
      };
      return FacilityService.AddTemplateSetting(queryModel);
    },
    {
      onSuccess: (response, row) => {
        if (response?.data.statusCode === 200) {
          toast.success(t(response?.data?.message));
          // Update local rows
          setLocalRows((curr: IRows[]) =>
            curr.map((r: IRows) =>
              r.templateId === row.templateId ? { ...r, rowStatus: false } : r
            )
          );
          // Invalidate and refetch template settings
          queryClient.invalidateQueries(QUERY_KEYS.templateSettings(queryParams));
          setValidation({ TemplateName: "", Lab: "" });
        }
      },
      onError: (error: any) => {
        console.error("Error saving template:", error);
        toast.error(t("Error saving template"));
      },
      onSettled: () => {
        setRequest(false);
        setCheck(false);
      },
    }
  );

  const validateRow = (row: IRows): boolean => {
    const newValidation = { TemplateName: "", Lab: "" };
    let isValid = true;

    if (!row.templateName) {
      newValidation.TemplateName = "Fill the required template Name";
      isValid = false;
    }
    if (!row.labId) {
      newValidation.Lab = "Select required Lab";
      isValid = false;
    }

    setValidation(newValidation);
    return isValid;
  };

  const handleChange = (name: string, value: string, templateId: number) => {
    setErrors(false);
    setCheck(true);

    // Clear validation for the specific field
    setValidation((prev) => ({
      ...prev,
      [name === "templateName" ? "TemplateName" : "Lab"]: "",
    }));

    setLocalRows((curr: IRows[]) =>
      curr.map((x: IRows) =>
        x.templateId === templateId ? { ...x, [name]: value } : x
      )
    );
  };
  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };
  const handleSearch = () => {
    // Clear any pending debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    // Trigger search immediately
    setDebouncedSearchRequest(searchRequest);
    setCurPage(1);
  };

  const resetSearch = () => {
    // Clear any pending debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    // Reset both search request and debounced search request
    setSearchRequest(INITIAL_SEARCH_QUERY);
    setDebouncedSearchRequest(INITIAL_SEARCH_QUERY);
    setSorting(INITIAL_SORTING_TYPE);
    setCurPage(1);
  };

  const handleSubmit = async (row: IRows) => {
    if (!validateRow(row)) return;
    setErrors(row.templateId < 0);
    setRequest(true);
    saveTemplateMutation.mutate(row);
  };

  const handleAddTemplate = () => {
    if (buttonClicked) return;
    setButtonClicked(true);
    setLocalRows((prevRows: IRows[]) => [
      {
        labId: 0,
        templateName: "",
        rowStatus: true,
        templateId: 0,
        isActive: true,
      },
      ...prevRows,
    ]);
  };

  const searchRef = useRef<any>(null);

  const handleSort = (columnName: any) => {
    const currentOrder = searchRef.current.id;
    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    searchRef.current.id = newOrder;

    setSorting({
      sortingOrder: newOrder,
      clickedIconData: columnName,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Trigger search on Enter key
      handleSearch();
    }
  };

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prev) => ({
      ...prev,
      [clickedTag]: (INITIAL_SEARCH_QUERY as any)[clickedTag],
    }));
  };

  useEffect(() => {
    const activeTags = Object.entries(searchRequest)
      .filter(([, value]) => value)
      .map(([key]) => key);
    setSearchedTags(activeTags);
  }, [searchRequest]);



  return (
    <>
      <div className="app-content flex-column-fluid p-0">
        <div className="card-body py-2">
          <div className="d-flex gap-4 flex-wrap mb-1">
            {searchedTags.map((tag) =>
              tag === "isArchived" ? null : (
                <div
                  key={tag}
                  className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                  onClick={() => handleTagRemoval(tag)}
                >
                  <span className="fw-bold">
                    {t(QUERY_DISPLAY_TAG_NAMES[tag])}
                  </span>
                  <i className="bi bi-x"></i>
                </div>
              )
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
            <div className="d-flex gap-2 responsive-flexed-actions">
              <div className="d-flex align-items-center">
                <span className="fw-400 mr-3">{t("Records")}</span>
                <select
                  id={`IDResultDataPreconfTemplateSettingRecords`}
                  className="form-select w-125px h-33px rounded py-2"
                  data-kt-select2="true"
                  data-placeholder="Select option"
                  data-dropdown-parent="#kt_menu_63b2e70320b73"
                  data-allow-clear="true"
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="50" selected>
                    50
                  </option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start">
                <div className="mt-0">
                  <PermissionComponent
                    moduleName="Oncology LIS"
                    pageName="Result Data Preconfiguration"
                    permissionIdentifier="AddNewTemplate"
                  >
                    <button
                      id={`IDResultDataPreconfTemplateSettingAddNew`}
                      onClick={handleAddTemplate}
                      className="btn btn-primary btn-sm fw-bold mr-sm-3  text-capitalize"
                      disabled={loading || buttonClicked}
                    >
                      <i style={{ fontSize: "16px" }} className="fa">
                        &#xf067;
                      </i>
                      {t("Add New Template")}
                    </button>
                  </PermissionComponent>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                id={`IDResultDataPreconfTemplateSettingSearch`}
                onClick={handleSearch}
                className="btn btn-linkedin btn-sm fw-500 py-2 rounded-3"
                aria-controls="Search"
              >
                {t("Search")}
              </button>
              <button
                onClick={resetSearch}
                type="button"
                className="btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
                id={`IDResultDataPreconfTemplateSettingReset`}
              >
                <span>
                  <span>{t("Reset")}</span>
                </span>
              </button>
            </div>
          </div>
          <div className="card">
            <Box sx={{ height: "auto", width: "100%" }}>
              <div className="table_bordered overflow-hidden">
                <TableContainer
                  sx={
                    isMobile
                      ? {
                        overflowY: "hidden",
                      }
                      : {}
                  }
                  className="shadow-none"
                >
                  <Table
                    aria-label="sticky table collapsible"
                    className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                  >
                    <TableHead>
                      <TableRow className="h-40px">
                        <TableCell></TableCell>
                        <TableCell className="min-w-50px"></TableCell>
                        <TableCell>
                          <input
                            id={`IDResultDataPreconfTemplateSettingTemplateName`}
                            type="text"
                            name="templateName"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search...")}
                            value={searchRequest.templateName}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                        <TableCell>
                          <select
                            id={`IDResultDataPreconfTemplateSettingLab`}
                            name="labId"
                            className="form-select bg-white rounded-2 fs-8 h-30px py-2"
                            value={searchRequest.labId}
                            onChange={onInputChangeSearch}
                          //onKeyDown={handleKeyPress}
                          >
                            <option value="">{t("Select an option")}</option>
                            {dropDownValues.referenceLab.map((option: any) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="h-30px">
                        <TableCell className="w-20px min-w-20px"></TableCell>
                        <TableCell className="w-50px min-w-50px text-center">
                          {t("Actions")}
                        </TableCell>
                        <TableCell
                          className="min-w-300px w-300px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("templateName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Template Name")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "templateName"
                                  ? "text-success fs-7"
                                  : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "templateName"
                                  ? "text-success fs-7"
                                  : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="min-w-300px w-300px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("labName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Lab")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "labName"
                                  ? "text-success fs-7"
                                  : "text-gray-700 fs-7"
                                  }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "labName"
                                  ? "text-success fs-7"
                                  : "text-gray-700 fs-7"
                                  }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="min-w-100px w-100px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("labName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Inactive/Active")}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading || isFetching ? (
                        <TableCell colSpan={9} className="padding-0">
                          <Loader />
                        </TableCell>
                      ) : !localRows.length ? (
                        <NoRecord message={"No data found for this table"} />
                      ) : (
                        localRows.map((item: any, index: any) => {
                          return (
                            <Row
                              key={item.templateId}
                              row={item}
                              index={index}
                              rows={localRows}
                              setRows={setLocalRows}
                              dropDownValues={dropDownValues}
                              handleChange={handleChange}
                              handleSubmit={handleSubmit}
                              setErrors={setErrors}
                              errors={errors}
                              request={request || saveTemplateMutation.isLoading}
                              setRequest={setRequest}
                              check={check}
                              setCheck={setCheck}
                              loadGridData={() => {
                                queryClient.invalidateQueries(QUERY_KEYS.templateSettings(queryParams));
                              }}
                              setButtonClicked={setButtonClicked}
                              validation={validation}
                              setValidation={setValidation}
                            />
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Box>
          </div>

          <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
            <p className="pagination-total-record mb-0">
              {Math.min(pageSize * curPage, total) === 0 ? (
                <span>
                  {t("Showing 0 to 0 of")} {total} {t("entries")}
                </span>
              ) : (
                <span>
                  {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
                  {Math.min(pageSize * curPage, total)} {t("of Total")}{" "}
                  <span> {total} </span> {t("entries")}{" "}
                </span>
              )}
            </p>
            <ul className="d-flex align-items-center justify-content-end custome-pagination mb-0 p-0">
              <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
                <i className="fa fa-angle-double-left"></i>
              </li>
              <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
                <i className="fa fa-angle-left"></i>
              </li>

              {pageNumbers.map((page) => (
                <li
                  key={page}
                  className={`px-2 ${page === curPage
                    ? "font-weight-bold bg-primary text-white h-33px"
                    : ""
                    }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => showPage(page)}
                >
                  {page}
                </li>
              ))}

              <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
                <i className="fa fa-angle-right"></i>
              </li>
              <li
                className="btn btn-lg p-2 h-33px"
                onClick={() => {
                  if (totalPages === 0) {
                    showPage(curPage);
                  } else {
                    showPage(totalPages);
                  }
                }}
              >
                <i className="fa fa-angle-double-right"></i>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
